//This component is an alternate login flow for internal use only. It allows passing a token and an accountId via url query parameters to allow us to login to customer accounts.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getQueryParamsFromLocation } from 'utils/urlHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { addSnackbarAlerts } from 'reducers/globalSlice';
import { TOKEN, CURRENT_ACCOUNT_ID } from 'constants/localStorageConstants';
import {
	setToken,
	setCurrentAccountId,
	userAccountsSelector,
	accountsPaginationSelector
} from 'reducers/userAuthSlice';
import { fetchAccounts } from 'apis/accounts.api';
import { useTranslation } from 'react-i18next';

const DebugAuth = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const queryParameters = getQueryParamsFromLocation(location);
	const token = queryParameters.token;
	const accountId = queryParameters.accountId;
	const accounts = useSelector(userAccountsSelector);
	const accountsPagination = useSelector(accountsPaginationSelector);
	const [accountsLoaded, setAccountsLoaded] = useState(false);

	const directUserAfterLogin = useCallback(
		accountsList => {
			if (accountsList.length === 0) {
				dispatch(
					addSnackbarAlerts([
						{
							type: 'error',
							message: t('No accounts found.')
						}
					])
				);
			} else if (accountId) {
				const selectedAccount = accountsList.find(account => account.id === Number(accountId));
				if (selectedAccount) {
					localStorage.setItem(CURRENT_ACCOUNT_ID, selectedAccount.id);
					dispatch(setCurrentAccountId(selectedAccount.id));
					return navigate(`/account/${accountId}/airspace`);
				} else {
					//accountId passed in didn't match one of the user's accounts
					return navigate('/account-picker');
				}
			} else {
				//No accountId passed in
				return navigate('/account-picker');
			}
		},
		[dispatch, navigate, accountId, t]
	);

	const getAccounts = useCallback(
		async page => {
			await dispatch(fetchAccounts(page));
		},
		[dispatch]
	);

	useEffect(() => {
		if (token) {
			localStorage.setItem(TOKEN, token);
			dispatch(setToken(token));
			const getAccounts = async page => {
				await dispatch(fetchAccounts(page));
			};
			if (!accounts && !accountsLoaded) {
				// Get first set of accounts
				getAccounts(1);
			} else if (
				accounts &&
				!accountsLoaded &&
				accountsPagination?.current_page !== accountsPagination?.last_page &&
				accountsPagination?.last_page !== 0
			) {
				// Incrementally load all pages of accounts
				getAccounts(accountsPagination.current_page + 1);
			} else if (accounts) {
				directUserAfterLogin(accounts);
				setAccountsLoaded(true);
			} else {
				setAccountsLoaded(true);
			}
		}
	}, [
		accounts,
		accountsLoaded,
		accountsPagination,
		directUserAfterLogin,
		dispatch,
		getAccounts,
		token
	]);

	return null;
};

export default DebugAuth;
